/**
 * DailyLogGuard — Garde global du verrou de saisie quotidienne (P3-P4).
 *
 * Pilote l'UX de verrouillage :
 *   - Poll `GET /api/team/daily-log/my-status` toutes les POLL_MS ms.
 *     Réponse : { ok, status: { in_scope, reference_date, validated, phase,
 *                 lock_at_iso, minutes_to_lock, locked } }.
 *   - Écoute le `CustomEvent('daily-log-locked')` émis par apiFetch sur un 423.
 *   - L'émet aussi quand c'est le SONDAGE qui découvre le verrou (13 h, sans aucun 423) : la
 *     visite guidée en cours se ferme sur ce signal (05/10/2026), sinon son voile recouvrirait
 *     l'écran de blocage. Une fois par passage au verrou ; `source: 'my-status'` évite que la
 *     garde ne relise son propre signal.
 *
 * Deux modes UI :
 *   - phase `reminder` → bannière sticky avec compte à rebours vers lock_at_iso.
 *   - `locked`        → overlay plein écran masquant tout, embarque
 *                        <DailyLogSection> pour saisir + valider (auto-déverrouillage).
 *
 * Monté dans App.js juste après <MaintenanceBanner />, en frère de la sidebar /
 * du <main> (hors <Outlet>) pour survivre aux changements de route.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import DailyLogSection from './DailyLogSection';

const POLL_MS = 60000;
// Écran de blocage : au-dessus de tout, SAUF les questions et messages (shared-ui, 100010+), qu'il
// doit laisser voir — sinon la suppression d'une ligne attendait une confirmation invisible (20/10/2026).
export const Z_ECRAN_BLOCAGE = 100000;

function formatCountdown(lockAtIso) {
  if (!lockAtIso) return '';
  const ms = new Date(lockAtIso).getTime() - Date.now();
  if (ms <= 0) return 'maintenant';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`;
}

function formatDayMonth(isoDate) {
  const [, m, d] = String(isoDate || '').split('-');
  return d && m ? `${d}/${m}` : String(isoDate || '');
}

// « vendredi 16/10 » : la journée demandée n'est pas toujours « hier » (week-end, absence,
// école d'un alternant). Incident 20/10/2026 : « 2026-10-19 » + un lien « saisie d'hier » qui
// ouvrait AUJOURD'HUI → Nassim a saisi son vendredi sur le mardi.
function formatJour(isoDate) {
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(isoDate || '');
  return `${d.toLocaleDateString('fr-FR', { weekday: 'long' })} ${formatDayMonth(isoDate)}`;
}

export default function DailyLogGuard() {
  const { user, logout } = useAuth();
  const email = user?.email || '';
  const [status, setStatus] = useState(null);
  const [, forceTick] = useState(0);
  const pollRef = useRef(null);
  // Verrou déjà signalé (par un 423 ou par ce sondage) : on ne le signale qu'au passage.
  const verrouSignale = useRef(false);

  const refresh = useCallback(async () => {
    if (!email) return;
    try {
      const r = await apiFetch('/api/team/daily-log/my-status');
      if (r.ok) {
        const d = await r.json();
        if (d.ok) {
          const verrouille = !!(d.status && d.status.locked);
          if (verrouille && !verrouSignale.current) {
            verrouSignale.current = true;
            window.dispatchEvent(new CustomEvent('daily-log-locked', {
              detail: { reference_date: d.status.reference_date, source: 'my-status' },
            }));
          }
          if (!verrouille) verrouSignale.current = false;
          setStatus(d.status);
        }
      }
    } catch (_) { /* réseau : on garde l'état précédent */ }
  }, [email]);

  useEffect(() => {
    if (!email) return undefined;
    refresh();
    pollRef.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [email, refresh]);

  useEffect(() => {
    const onLocked = (e) => {
      verrouSignale.current = true;
      setStatus((prev) => ({ ...(prev || {}), locked: true, phase: 'locked',
        reference_date: e.detail?.reference_date || prev?.reference_date }));
      // Son propre signal : le statut vient d'être lu, pas de relecture.
      if (e.detail?.source !== 'my-status') refresh();
    };
    window.addEventListener('daily-log-locked', onLocked);
    return () => window.removeEventListener('daily-log-locked', onLocked);
  }, [refresh]);

  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  if (!status || !status.in_scope) return null;

  if (status.locked) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: Z_ECRAN_BLOCAGE, background: 'rgba(15,23,42,0.97)',
        overflowY: 'auto', padding: '24px',
      }}>
        <div style={{ maxWidth: 920, margin: '0 auto', background: '#fff', borderRadius: 12, padding: 24 }}>
          <h2 style={{ marginTop: 0, color: '#b91c1c' }}>⛔ Accès suspendu — saisie de la veille requise</h2>
          <p>
            Ta journée du <strong>{formatJour(status.reference_date)}</strong> n'est pas validée.
            Complète-la et clique « Valider ma journée » : ton accès se rouvrira automatiquement.
          </p>
          <DailyLogSection userEmail={email} lockedDate={status.reference_date} />
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <button onClick={refresh} style={{ marginRight: 12 }}>J'ai validé, réessayer</button>
            <button
              type="button"
              onClick={async () => { try { await logout(); } finally { window.location.href = '/'; } }}
              style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  // `phase` est purement horaire côté backend (9h→13h = reminder) : c'est le
  // croisement avec `validated` qui décide de l'affichage — sinon la bannière
  // reste toute la matinée même après validation (incident Ahmed 2026-06-12).
  if (status.phase === 'reminder' && !status.validated) {
    return (
      <div style={{
        position: 'sticky', top: 0, zIndex: 9000, background: '#fef3c7',
        borderBottom: '1px solid #f59e0b', padding: '8px 16px', fontSize: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {status.grace_until ? (
          // Délai de grâce (15/09/2026) : aucune heure de blocage n'existe, on
          // n'en annonce pas une.
          <span>
            📝 Ta saisie du <strong>{formatJour(status.reference_date)}</strong> n'est pas validée —
            pense à la compléter (pas de blocage d'accès jusqu'au{' '}
            <strong>{formatDayMonth(status.grace_until)}</strong> inclus).
          </span>
        ) : (
          <span>
            ⏰ Ta saisie du <strong>{formatJour(status.reference_date)}</strong> n'est pas validée —
            accès bloqué dans <strong>{formatCountdown(status.lock_at_iso)}</strong>.
          </span>
        )}
        {/* Le lien ouvre la journée DEMANDÉE, pas celle du jour (incident du 20/10/2026). */}
        <a href={`/reports/my-interventions?saisie=${encodeURIComponent(status.reference_date || '')}`}>
          Compléter ma saisie du {formatJour(status.reference_date)} →
        </a>
      </div>
    );
  }

  return null;
}
